<template>
    <v-row
        no-gutters
        class="pl-9 pt-10"
    >
        <v-col
            cols="9"
            class="pr-10"
        >
            <v-row
                class="mb-8"
                no-gutters
            >
                <v-col
                    cols="12"
                    class="title"
                >
                    {{ $t('Connect accounting service') }}
                </v-col>
            </v-row>

            <v-row
                no-gutters
                align="center"
                class="mb-3"
            >
                <v-col
                    cols="4"
                >
                    {{ $t("Service") }}
                </v-col>

                <v-col
                    cols="8"
                >
                    <v-select
                        v-model="form_data.vendor"
                        :items="enabled_vendors"
                        item-text="text"
                        item-value="value"
                        dense
                        solo
                        offset-y
                        hide-details="auto"
                        :disabled="form_disabled"
                        :error-messages="form_data.vendor_error"
                    />
                </v-col>
            </v-row>

            <v-row
                v-if="show_form_field('api_country')"
                no-gutters
                align="center"
                class="mb-3"
            >
                <v-col cols="4">
                    {{ get_field_translations(form_data.vendor, 'api_country') }}
                </v-col>
                <v-col
                    cols="8"
                >
                    <v-select
                        v-model="form_data.api_country"
                        :disabled="form_disabled"
                        :error-messages="form_data.api_country_error"
                        :placeholder="$t('Please choose your country')"
                        :items="merit_aktiva_api_countries"
                        :item-text="(x)=>$t(x.text)"
                        item-value="value"
                        dense
                        solo
                        hide-details="auto"
                    />
                </v-col>
            </v-row>

            <v-row
                v-if="show_form_field('api_key')"
                no-gutters
                align="center"
                class="mb-3"
            >
                <v-col cols="4">
                    {{ get_field_translations(form_data.vendor, 'api_key') }}
                </v-col>
                <v-col
                    cols="8"
                >
                    <v-text-field
                        v-model.trim="form_data.api_key"
                        :disabled="form_disabled"
                        :error-messages="form_data.api_key_error"
                        dense
                        solo
                        hide-details="auto"
                        :append-icon="api_key_visibility ? 'visibility' : 'visibility_off'"
                        :type="api_key_visibility ? 'password' : 'text'"
                        @click:append="() => (api_key_visibility = !api_key_visibility)"
                    />
                </v-col>
            </v-row>

            <v-row
                v-if="show_form_field('api_secret')"
                align="center"
                no-gutters
                class="mb-3"
            >
                <v-col cols="4">
                    {{ get_field_translations(form_data.vendor, 'api_secret') }}
                </v-col>
                <v-col
                    cols="8"
                >
                    <v-text-field
                        v-model.trim="form_data.api_secret"
                        :disabled="form_disabled"
                        :error-messages="form_data.api_secret_error"
                        dense
                        solo
                        hide-details="auto"
                        :append-icon="api_secret_visibility ? 'visibility' : 'visibility_off'"
                        :type="api_secret_visibility ? 'password' : 'text'"
                        @click:append="() => (api_secret_visibility = !api_secret_visibility)"
                    />
                </v-col>
            </v-row>

            <v-row
                v-if="show_form_field('subdomain')"
                align="center"
                no-gutters
                class="mb-3"
            >
                <v-col cols="4">
                    {{ get_field_translations(form_data.vendor, 'subdomain') }}
                </v-col>
                <v-col
                    cols="8"
                >
                    <v-text-field
                        v-model="form_data.subdomain"
                        :disabled="form_disabled"
                        hide-details="auto"
                        dense
                        solo
                        :error-messages="form_data.subdomain_error"
                    />
                </v-col>
            </v-row>


            <v-row
                v-if="form_data.vendor"
                no-gutters
                class="mt-5"
            >
                <v-col class="text-right">
                    <v-btn
                        v-if="showTestConnection"
                        outlined
                        color="primary"
                        :disabled="canContinue()"
                        :loading="loading"
                        class="ml-4"
                        @click="test_connection"
                    >
                        {{ $t("Test connection") }}
                    </v-btn>
                </v-col>
            </v-row>
            <v-row
                v-if="updateInvoiceStatusVisible"
                no-gutters
            >
                <v-col cols="12">
                    <BillingDialogInvoiceStatusOptions
                        :status-update-datetime.sync="form_data.invoice_settings.status_update_datetime"
                        :status-update-enabled.sync="form_data.invoice_settings.status_update_enabled"
                        :can-update-now="false"
                        @update_invoice_statuses="handleInvoiceUpdate"
                    />
                </v-col>
            </v-row>
            <v-row no-gutters class="mt-4">
                <v-col cols="12" class="d-flex">
                    <v-spacer/>
                    <v-btn
                        color="primary"
                        :disabled="canContinue()"
                        :loading="loading"
                        class="ml-4"
                        @click="save"
                    >
                        {{ $t("Save") }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-col>
        <v-col cols="3">
            <v-row
                class="n1 sidebar_block"
                no-gutters
                justify="center"
                align="center"
            >
                <v-col class="shrink">
                    <v-row
                        no-gutters
                        class="flex-nowrap"
                        align="center"
                    >
                        <v-col class="mr-3">
                            <v-icon color="primary">
                                mdi-book-open-blank-variant
                            </v-icon>
                        </v-col>
                        <v-col
                            class="primary--text body-2"
                            style="white-space: nowrap"
                        >
                            <a :href="HelpModule.getHelpLink('app_billing')" target="_blank">
                                {{ $t('How to set up') }}
                            </a>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import axios from "axios";
import {$t} from "@/i18n";
import {computed, toRefs, ref, reactive} from "@vue/composition-api";
import AppModule from "@/store/modules/app";
import FeatureModule from "@/store/modules/feature";
import DialogModule from "@/store/modules/dialog";
import {APP_STORE_APP_TYPES} from "@/helpers/constants";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import {TAB_NAMES} from "@/views/AppStore/app-tabs";
import MeritAktivaModule from "@/store/modules/merit_aktiva";
import {getRegionAppTitle} from "../../../helpers/helpers";
import HelpModule from "@/modules/help/help"
import BillingDialogInvoiceStatusOptions from "@/components/AppStore/BillingDialogInvoiceStatusOptions";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import ApiInvoice from "@/api/billing/invoice";
import FeedbackNotificationModule from "@/store/modules/feedback_notification";


export default {
    name: "BillingDialogSetupTab",
    components: {
        BillingDialogInvoiceStatusOptions
    },
    setup(props, ctx) {
        const {handleErrorResponse, clearValidationErrors} = AppModule();
        const {execAppAction, fetchAllApps} = FeatureModule();
        const {api_countries: merit_aktiva_api_countries} = MeritAktivaModule()
        const {appStoreDialogGetters, appStoreDialogActions} = appStoreDialog()
        const {showFeedbackNotification} = FeedbackNotificationModule();
        const accountFeature = appStoreDialogGetters.getCurrentFeature()
        const accountFeatureSettings = appStoreDialogGetters.getCurrentFeatureSettings()
        const selectedAccount = computed(() => {
            return appStoreDialogGetters.getSelectedCompany()
        })

        const invoiceStatusUpdateNowVisible = ref(false);
        invoiceStatusUpdateNowVisible.value = Boolean(accountFeatureSettings && accountFeatureSettings.data && accountFeatureSettings.data.vendor && (accountFeatureSettings.data.vendor !== ''))

        const get_field_translations = function (vendor_name, field_name) {
            const translations = {
                [APP_STORE_APP_TYPES.MERIT_AKTIVA]: {
                    api_key: $t('API ID'),
                    api_secret: $t('API key'),
                    api_country: $t('Country'),
                    subdomain: $t('Subdomain')
                },
                [APP_STORE_APP_TYPES.SMART_ACCOUNTS]: {
                    api_key: $t('API ID'),
                    api_secret: $t('API key'),
                },
                [APP_STORE_APP_TYPES.SCORO]: {
                    api_key: $t('Company Account Id'),
                    api_secret: $t('Api secret'),
                    subdomain: $t('Subdomain')
                },
                [APP_STORE_APP_TYPES.ECONOMIC]: {}
            }
            return translations[vendor_name][field_name]
        }

        const form_data = ref({
            vendor: "",
            api_key: "",
            api_secret: "",
            api_country: '',
            subdomain: "",
            schedule: "",
            invoice_settings: {
                status_update_enabled: 0,
                status_update_datetime: ""
            }
        });
        const loading = ref(false);
        const state = {
            vendors: [
                {value: APP_STORE_APP_TYPES.SCORO, text: $t("Scoro"), fields: ['api_key', 'api_secret', 'subdomain'], auto_update_enabled: false},
                {value: APP_STORE_APP_TYPES.MERIT_AKTIVA, text: getRegionAppTitle(APP_STORE_APP_TYPES.MERIT_AKTIVA, selectedAccount.value.region_code), fields: ['api_key', 'api_secret', 'api_country'], auto_update_enabled: true},
                {value: APP_STORE_APP_TYPES.ECONOMIC, text: $t('E-conomic'), fields: [], auto_update_enabled: true},
                {value: APP_STORE_APP_TYPES.XERO, text: $t('Xero'), fields: [], auto_update_enabled: true},
                {value: APP_STORE_APP_TYPES.QUICKBOOKS, text: $t('QuickBooks'), fields: [], auto_update_enabled: true},
                {value: APP_STORE_APP_TYPES.TRIPLETEX, text: $t('Tripletex'), fields: [], auto_update_enabled: true},
                {value: APP_STORE_APP_TYPES.FORTNOX, text: $t('Fortnox'), fields: [], auto_update_enabled: true},
                {value: APP_STORE_APP_TYPES.SMART_ACCOUNTS, text: $t("SmartAccounts"), fields: ['api_key', 'api_secret'], auto_update_enabled: true},
            ]
        };

        const show_form_field = (field) => {
            if (form_data.value.vendor) {
                for (let i in state.vendors) {
                    let v = state.vendors[i].value;
                    if (v === form_data.value.vendor) {
                        for (let j in state.vendors[i].fields) {
                            let f = state.vendors[i].fields[j];
                            if (f === field) return true;
                        }
                    }
                }
                return false;
            }
        };

        const test_connection = () => {
            setConfirmOptions({
                title: $t("Test connection"),
                confirm_text: $t("Test"),
                cancel_text: $t("Cancel"),
                action: () => test_connection_action("test_connection"),
            });
            openConfirmDialog();
        };

        const form_disabled = computed(() => {
            return accountFeature.status !== "active"
        });
        const {setConfirmOptions, openConfirmDialog, setErrorOptions, openError} = DialogModule();

        const test_connection_action = async function (action) {
            loading.value = true;
            await execAppAction({
                company_id: selectedAccount.value.id,
                feature_id: accountFeature.feature_id,
                action: action,
                data: clean_and_clone_object(form_data.value)
            }).then(function (response) {
                loading.value = false;
                if (response.test_connection) {
                    showFeedbackNotification($t(response.title))
                } else {
                    setErrorOptions({
                        title: $t("Error"),
                        text: $t(response.title)
                    });
                    openError();
                }
                // ctx.emit("close");
            }).catch(ex => {
                console.error("Billing SettingsControl :: test_connection", ex);
                loading.value = false;
                handleErrorResponse(form_data.value, ex);
                // ctx.emit("close");
            });
        };
        const clean_and_clone_object = src => {
            let target = {};
            for (let prop in src) {
                if (Object.prototype.hasOwnProperty.call(src, prop)) {
                    if (prop.endsWith("_f") || prop.endsWith("_error")) continue;
                    target[prop] = src[prop];
                }
            }
            return target;
        };
        const save = async () => {
            loading.value = true;
            clearValidationErrors(form_data.value);
            await execAppAction({
                company_id: selectedAccount.value.id,
                feature_id: accountFeature.feature_id,
                action: "save_settings",
                data: clean_and_clone_object(form_data.value)
            }).then(resp => {
                loading.value = false;
                ctx.emit('tabUpdate', 'products');
                ctx.emit("close");
                invoiceStatusUpdateNowVisible.value = true
                appStoreDialogActions.updateCurrentTab(TAB_NAMES.PRODUCT)
            }).catch(ex => {
                loading.value = false;
                handleErrorResponse(form_data.value, ex);
            });
        };

        const load_data = async () => {
            if (!accountFeatureSettings) {
                return
            }

            if (!accountFeatureSettings.data) {
                return
            }

            if (accountFeatureSettings.data.invoice_settings) {
                form_data.value.invoice_settings.status_update_enabled = accountFeatureSettings.data.invoice_settings.status_update_enabled
                form_data.value.invoice_settings.status_update_datetime = accountFeatureSettings.data.invoice_settings.status_update_datetime
            }

            form_data.value.vendor = accountFeatureSettings.data.vendor
            form_data.value.api_country = accountFeatureSettings.data.api_country
            form_data.value.schedule = accountFeatureSettings.data.schedule
            apps.value = await fetchAllApps({company_id: selectedAccount.value.id})

            try {
                // todo :: refactor to feature.js api
                const {data} = await axios.post(`/a/${selectedAccount.value.id}/features/${accountFeature.feature_id}/setup_data`)
                if (data) {
                    form_data.value.api_key = data.api_key
                    form_data.value.api_secret = data.api_secret
                    form_data.value.subdomain = data.subdomain
                }
            } catch (ex) {
                errorDialog.showError(ex);
            }
        };
        const apps = ref([])
        const enabled_vendors = computed(() => {
            return state.vendors.filter((vendor) => {
                // filter out only those vendors that have been activated by the user and are still active
                return apps.value.some((app) => app.name === vendor.value && app.account_feature && app.account_feature.status === 'active')
            })
        })

        const handleInvoiceUpdate = function () {
            const payload = {
                integration_name: form_data.value.vendor,
                account_id: selectedAccount.value.id
            }

            ApiInvoice.updateInvoiceStatuses(payload)
        }

        const updateInvoiceStatusVisible = computed(() => {
            if (!form_data.value.vendor || form_data.value.vendor === '') return false
            const vendorInfo = state.vendors.find((vendor) => vendor.value === form_data.value.vendor)
            return vendorInfo ? vendorInfo.auto_update_enabled : false
        })

        const canContinue = function () {
            if (form_data.value.vendor === APP_STORE_APP_TYPES.MERIT_AKTIVA) {
                return form_disabled.value || !form_data.value.api_country
            } else {
                return form_disabled.value
            }
        }
        load_data();

        const showTestConnection = computed(() => ![APP_STORE_APP_TYPES.ECONOMIC, APP_STORE_APP_TYPES.TRIPLETEX, APP_STORE_APP_TYPES.FORTNOX].includes(form_data.value.vendor))

        return {
            ...toRefs(state),
            enabled_vendors,
            form_data,
            loading,
            form_disabled,
            accountFeature,
            accountFeatureSettings,
            show_form_field,
            test_connection,
            save,
            APP_STORE_APP_TYPES,
            api_key_visibility: String,
            api_secret_visibility: String,
            get_field_translations,
            merit_aktiva_api_countries,
            canContinue,
            showTestConnection,
            HelpModule,
            handleInvoiceUpdate,
            invoiceStatusUpdateNowVisible,
            updateInvoiceStatusVisible
        };
    }
};
</script>
